import isNil from "lodash/isNil";

import { ChannelPreview } from "@/constants/Messages";
import { ChannelCardTestID, TextTestID } from "@/constants/TestID";
import { TextSeparator } from "@/constants/Text";
import {
  BaseChannelPreviewCardContainer,
  Description,
  Title,
  UnreadIndicator,
} from "@/ui/components/messages/cards/CommonCardComponents";
import { Avatar, FAIconName, Icon, Text } from "@/ui/core";
import MessagesAvatarGroup from "@/ui/core/MessagesAvatarGroup";
import { HStack, VStack } from "@/ui/layout";
import { ColorToken } from "@/ui/theme/tokens";
import { joinTestIDs } from "@/utils/string.util";

interface ChannelPreviewCardProps {
  avatarUserId?: string;
  channelName: string;
  channelUrl: string;
  isGroupChannel?: boolean;
  isLastMessageFileMessage?: boolean;
  lastMessage?: string;
  lastMessageTime?: string;
  members: BaseUserAvatarProps[];
  onSelect: (channelUrl: string) => void;
  providerMembersDescription: string;
  unreadMessageCount: number;
}

interface LastMessagePreviewRowProps extends ViewWithTestID {
  isFileMessage?: boolean;
  lastMessage?: string;
  lastMessageTime?: string;
}

const LastMessagePreviewRow = ({
  isFileMessage,
  lastMessage,
  lastMessageTime,
  testID,
}: LastMessagePreviewRowProps) => {
  if (isNil(lastMessage)) {
    return (
      <Text
        color={ColorToken.NeutralSecondary}
        testID={joinTestIDs(testID, ChannelCardTestID.EmptyChannel)}
        variant="caption"
      >
        {ChannelPreview.EmptyChannel}
      </Text>
    );
  }

  return (
    <HStack gap={6}>
      {isFileMessage ? (
        <HStack alignItems="center" gap={2}>
          <Icon
            color={ColorToken.NeutralSecondary}
            name={FAIconName.File}
            size="sm"
            testID={joinTestIDs(testID, ChannelCardTestID.LastMessageFile)}
          />
          <Text
            color={ColorToken.NeutralSecondary}
            testID={joinTestIDs(testID, ChannelCardTestID.LastMessageFile)}
            variant="caption"
          >
            {ChannelPreview.FileMessage}
          </Text>
        </HStack>
      ) : (
        <Text
          color={ColorToken.NeutralSecondary}
          flexShrink={1}
          numberOfLines={1}
          testID={joinTestIDs(testID, ChannelCardTestID.LastMessage)}
          variant="caption"
        >
          {lastMessage}
        </Text>
      )}
      <HStack gap={6}>
        <Text
          color={ColorToken.NeutralSecondary}
          testID={joinTestIDs(
            testID,
            ChannelCardTestID.LastMessage,
            "separator",
          )}
          variant="caption"
        >
          {TextSeparator.Dot}
        </Text>
        <Text
          color={ColorToken.NeutralSecondary}
          flexShrink={0}
          testID={joinTestIDs(testID, ChannelCardTestID.LastMessage, "time")}
          variant="caption"
        >
          {lastMessageTime}
        </Text>
      </HStack>
    </HStack>
  );
};
/* eslint max-lines-per-function: ["error", { max: 160 }] -- high verbosity in this component*/
const ChannelPreviewCard = ({
  avatarUserId,
  channelName,
  channelUrl,
  isGroupChannel,
  isLastMessageFileMessage,
  lastMessage,
  lastMessageTime,
  members,
  onSelect,
  providerMembersDescription,
  unreadMessageCount,
}: ChannelPreviewCardProps) => {
  const shouldShowUnreadIndicator = unreadMessageCount > 0;

  const baseTestID = joinTestIDs(
    channelUrl,
    "channel-preview",
    TextTestID.Card,
  );

  return (
    <BaseChannelPreviewCardContainer
      avatarComponent={
        isGroupChannel ? (
          <MessagesAvatarGroup testID={baseTestID} users={members} />
        ) : (
          <Avatar
            size="sm"
            testID={joinTestIDs(baseTestID, "avatar")}
            userId={avatarUserId}
            userName={channelName}
            userType="provider"
          />
        )
      }
      onPress={() => onSelect(channelUrl)}
      testID={baseTestID}
    >
      <VStack flex={1} gap={2}>
        <HStack alignItems="center" gap={6} overflow="hidden">
          {shouldShowUnreadIndicator && <UnreadIndicator testID={baseTestID} />}
          {isGroupChannel && (
            <Icon
              name={FAIconName.UserGroupSolid}
              size="sm"
              testID={joinTestIDs(baseTestID, "group-channel")}
            />
          )}
          <Title
            testID={joinTestIDs(baseTestID, ChannelCardTestID.ChannelName)}
          >
            {channelName}
          </Title>
          {!isGroupChannel && (
            <>
              <Text
                color={ColorToken.NeutralSecondary}
                testID={joinTestIDs(
                  baseTestID,
                  ChannelCardTestID.ChannelName,
                  "separator",
                )}
                variant="caption"
              >
                {TextSeparator.Dash}
              </Text>
              <Description
                testID={joinTestIDs(
                  baseTestID,
                  ChannelCardTestID.ProviderNames,
                )}
              >
                {providerMembersDescription}
              </Description>
            </>
          )}
        </HStack>
        <LastMessagePreviewRow
          isFileMessage={isLastMessageFileMessage}
          lastMessage={lastMessage}
          lastMessageTime={lastMessageTime}
          testID={baseTestID}
        />
      </VStack>
    </BaseChannelPreviewCardContainer>
  );
};

export type { ChannelPreviewCardProps };
export default ChannelPreviewCard;
